import {ApexOptions} from "apexcharts";
import {ApexMonthRanger} from "@/helper/apexMonthRanger";
import {mergeDeep} from "@/helper/object";

export const APEX_HEIGHT = '180px'
export const APEX_SKELETON = `block--height-${APEX_HEIGHT}`
export const APEX_SKELETON_WITH_SUMMARY = 'block--height-260px'
export const APEX_SKELETON_DOUBLE_WITH_SUMMARY = 'block--height-455px'

export type ApexOptionsWrapped = ApexOptions & {chart: ApexChart} & {series: ApexAxisChartSeries | ApexNonAxisChartSeries}

type ApexDataPointIndexParameter = number | string | undefined
type ApexChartContext = ApexCharts & {w:{globals:any,config:any}}

/**
 *   chartType: 'line' | 'bar
 *   stroke: { curve: 'smooth' | 'stepline' | 'straight' }
 *
 *
 */
export const generateApexOptions = <xType>(
    monthRanger: ApexMonthRanger<any>,
    apexOptions: ApexOptionsWrapped = {chart:{}, series: []},
    annotator : ((index:number) => string | null | undefined) | undefined = undefined
) : ApexOptionsWrapped => {

    const categories = monthRanger.getMonthLabels()

    const markers = () => {
        const defaultMarkers = {
            size: 6
        }

        return (apexOptions.chart.type === 'line' || apexOptions.chart.type === 'area')
            ? mergeDeep(defaultMarkers, {
                discrete: categories.map((value: string, _index: number) => ({
                    seriesIndex: 0,
                    dataPointIndex: _index,
                    fillColor: monthRanger.selectedIndex.value === _index ? 'var(--ion-color-red)' : 'var(--ion-color-grey)',
                    strokeColor: 'var(--ion-color-white)',
                    size: monthRanger.selectedIndex.value === _index ? 8 : 5,
                    shape: "circle" as ApexMarkerShape
                }))
            })
            : defaultMarkers
    }

    const xaxis = () : ApexXAxis => ({
        categories,
        crosshairs: {
            show: false,
            // width: 1,
            // stroke: {
            //     color: '#999',
            //     width: 1,
            //     dashArray: 4 // <- dashed style
            // }
        },
        tooltip: { enabled: false },
        labels: { style: { colors: categories.map((value:string, _index:number) => monthRanger.selectedIndex.value === _index ? 'var(--ion-color-red-shade)' : monthRanger.initialSelectedIndex.value === _index ? 'var(--ion-color-red-tint)' : 'var(--ion-color-grey)') }}
    } as ApexXAxis)

    const annotations = () : ApexAnnotations => {
        let value = undefined
        if (annotator) {
            value = annotator(monthRanger.selectedIndex.value)
        } else {
            // TODO: suboptimal
            // let value = chart.w.globals.series[0][index]
            // @ts-ignore
            const seriesData = apexOptions.series?.[0]?.data
            value = seriesData && Array.isArray(seriesData) ? seriesData[monthRanger.selectedIndex.value] : undefined

            // @ts-ignore
            if (value !== undefined && apexOptions.yaxis?.labels?.formatter) {
                // @ts-ignore
                value = apexOptions.yaxis?.labels?.formatter(value)
            }
        }

        return { xaxis : [ {
            x: categories[monthRanger.selectedIndex.value],
            borderColor: 'var(--ion-color-red)',
            fillColor: 'var(--ion-color-red)',
            borderWidth: 1,
            strokeDashArray: 4,
            label: {
                borderWidth: 0,
                text: value ?? '?',
                textAnchor: 'middle',
                position: 'top',
                orientation: 'horizontal',
                style: {
                    color: 'var(--ion-color-red-shade)'
                }
            }
        } ] }
    }

    const onIndexClick = (index: ApexDataPointIndexParameter, chart: ApexChartContext) => {
        // set index as selected index
        if (index === undefined) {
            return
        }
        if (typeof index === 'string') {
            index = parseInt(index)
        }
        monthRanger.selectIndex(index)

        chart.updateOptions({
            xaxis: xaxis(),
            markers: markers(),
            annotations: annotations()
        })

        // trigger color update after index select
        // chart.resetSeries()
    }

    const colors = apexOptions.chart.type && ['bar','pie','donut','radialBar'].includes(apexOptions.chart.type)
        ? [
            ({value, dataPointIndex} : {value: number , dataPointIndex : ApexDataPointIndexParameter}) => {
                const baseColorPositive = 'var(--ion-color-grey)';
                const baseColorNegative = 'var(--ion-color-grey-tint-1)';
                const selectedColorPositive = 'var(--ion-color-red)';
                const selectedColorNegative = 'var(--ion-color-red-tint-3-shade)';

                if (dataPointIndex !== undefined && monthRanger.selectedIndex.value == (dataPointIndex as number | string)) {
                    return value >= 0 ? selectedColorPositive : selectedColorNegative;
                }

                return !value || value >= 0 ? baseColorPositive : baseColorNegative;
            }
        ]
        : ['var(--ion-color-grey)']

    const defaultOptions : ApexOptions = {
        fill: {
            gradient: {
                opacityFrom: 0.6,
                opacityTo: 0,
                inverseColors: false, // lineColor -> gradientToColor
                gradientToColors: ['var(--ion-color-red)']
            },
        },
        stroke: { curve: 'monotoneCubic' },
        chart: {
            height: APEX_HEIGHT,
            width: '100%',
            toolbar: { show: false },
            zoom: { enabled: false },

            events: {
                dataPointSelection: (pe:PointerEvent, chart: ApexChartContext, options: {dataPointIndex:ApexDataPointIndexParameter} ) => onIndexClick(options.dataPointIndex, chart),
                markerClick: (pe:PointerEvent, chart: ApexChartContext, options: {dataPointIndex:ApexDataPointIndexParameter} ) => onIndexClick(options.dataPointIndex, chart),
                xAxisLabelClick: (pe:PointerEvent, chart: ApexChartContext, options: {labelIndex:ApexDataPointIndexParameter} ) => onIndexClick(options.labelIndex, chart),
                // click: (pe:PointerEvent, chart: ApexChartContext, options: {dataPointIndex:ApexDataPointIndexParameter} ) => console.log('apex click on index %s', options.dataPointIndex),
            },

            animations: {
                enabled: true,
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },

        colors,

        annotations: annotations(),

        xaxis: xaxis(),
        yaxis: {
            // min: series.some(s => s.data.some((d:any) => d < 0)) ? undefined : 0,
            // max: maxY,
            axisBorder: { show: false },
            opposite: true, // y-axis on right side
            labels: { style: { colors: 'var(--ion-color-grey)' }}
        },

        markers: markers(),

        states: {
            hover: {
                filter: {
                    type: 'none'
                }
            },
            active: {
                filter: {
                    type: 'none'
                }
            }
        },


        grid: {
            yaxis: {
                lines: {show: false} // hide horizontal grid lines
            },
            // padding: {
            //     // bars are wide enough to accomodate labels and skip the padding
            //     left: apexOptions.chart.type === 'bar' ? 0 : 10,
            //     right: apexOptions.chart.type === 'bar' ? 0 : 10
            // }
        },
        dataLabels: {
            enabled: false // <- this hides the numbers inside the bars
        },
        legend: {
            show: false // <- hides the legend
        },
        tooltip: {
            enabled: true,
            x: {
                show: false // <- hides the tooltip label on the x-axis
            },
            marker: {
                show: false // optional if you want to hide marker tooltip dot
            },
            custom: function ({series, seriesIndex, dataPointIndex, w}:{series:any, seriesIndex:any, dataPointIndex:any, w:any}) {
                return '<div></div>'
            },
            // intersect: true, // enable dataPointSelection
            // shared: false // enable dataPointSelection
        }
    }

    return mergeDeep(defaultOptions, /*selection, */ apexOptions) as ApexOptionsWrapped
}
